import React from "react";
import Button from "react-bootstrap/Button";
import Video, { createLocalTracks } from "twilio-video";

import "./Videopart.css";
import Participant from "./Participant";
import AppLoader from "../AppLoader/AppLoader";
import { useAppDispatch, useAppSelector } from "../../core/redux/hooks";
import vrm_large_logo from "../../assets/vrm_large_logo.png";
import { DtoVideoRoomsProps } from "../../core/dto/dashboard.dto";
import AppModal from "../AppModal/AppModal";
import { PostApiMethod } from "../../core/services/apiService";
import InputMask from "react-input-mask";
import { DisplayToaster, numberOnly, RevertFormattedPhoneNumber, DisplayErrorMessages, loggerHelper, getMobileOperatingSystem } from "../../core/services/sharedService";
import Teams from "../Teams/Teams";
import DeviceChoice from "../Settings/DeviceChoice";
import { ParticipantAudioTracks } from "./ParticipantAudioTracks";
import { DEFAULT_VIDEO_CONSTRAINTS, DEFAULT_AUDIO_CONSTRAINTS } from '../../constants';
import { setEnableDisableDropDown, setInHandOffCall, triggerVideoCallEndFunction } from "../../core/redux/notificationsSlice";
import AgentStatusInfoTooltip from "./AgentStatusInfoTooltip";

const VideoRoom = ({
    setLocalParticipantInfo,
    roomName,
    roomSid,
    addParticipant,
    twilioToken,
    locationId,
    notificationId,
    terminateVideoCall,
    isKiosk = false,
    terminateVideoCallRef,
    holdVideoCallRef,
    agentName,
    agentNameColor,
    isHandOffRoom,
    isQrTenantRoom = false,
    terminateHandOffCall,
    terminateQrRoomCall,
    putCallOnHold,
    inHoldDuration,
    agentId,
    addParticipantVideoCall,
    isQrScanNotification,
    remoteParticipantConnectedFun
}: DtoVideoRoomsProps) => {
    const [room, setRoom] = React.useState<any>(null);
    const [audioDisable, setAudioDisable] = React.useState(false);
    const [videoDisable, setVideoDisable] = React.useState(false);
    const [participants, setParticipants] = React.useState<any>([]);
    const [connectVideoRoom, setConnectVideoRoom] = React.useState<boolean>(true);
    const [connectVideoRoomCount, setConnectVideoRoomCount] = React.useState<number>(1);
    const [timer, setTimer] = React.useState<number>(0);
    const [startTimer] = React.useState<boolean>(true);
    let participantConnectedRef = React.useRef<boolean>(false);
    const [showPopUp, setShowPopUp] = React.useState<boolean>(false);
    const videoCallHandoffNumberRef = React.useRef<string>();
    const [handOffContactError, setHandOffContactError] = React.useState<boolean>(false);
    const [contactMinMaxError, setContactMinMaxError] = React.useState<boolean>(false);
    const kioskColorInfo = React.useRef<{ [index: string]: string }>();
    const [addThirdUserFlag, setAddThirdUserFlag] = React.useState<boolean>(false);
    const [localAudioTrack, setLocalAudioTrack] = React.useState<any>();
    const [camerasFacingMode, setCamerasFacingMode] = React.useState<boolean>(false);
    const [handOffLoader, setHandOffLoader] = React.useState(false);
    let notificationsSliceSelector = useAppSelector(
        (state: any) => state.agentNotification
    );

    let roomDataRef = React.useRef();

    let dispatch = useAppDispatch();
    const logger = React.useCallback((message: String, details: any) => {
        loggerHelper(message, details);
    }, [])

    React.useEffect(() => {
        if (agentNameColor) {
            kioskColorInfo.current = { 'color': agentNameColor };
        }
    }, [agentNameColor, twilioToken]);

    const detachTracks = (tracks: any) => {
        tracks.forEach((tracking: any) => {
            // tracking?.stop();
            tracking.detach().forEach((detachedElement: any) => {
                // detachedElement.remove();
            });
        });
    };

    const getTracks = (participant: any) => {
        return Array.from(participant?.tracks.values())
            .filter(function (publication: any) {
                return publication.track;
            })
            .map(function (publication: any) {
                return publication.track;
            });
    };

    const detachParticipantTracks = React.useCallback((participant: any) => {
        let tracks = getTracks(participant);
        detachTracks(tracks);
    }, []);

    const muteAudioTracks = React.useCallback(
        (status: boolean) => {
            room.localParticipant.audioTracks.forEach((publication: any) => {
                if (status) {
                    publication.track.enable();
                } else {
                    publication.track.disable();
                }
            });
        },
        [room]
    );

    const muteVideoTracks = React.useCallback(
        (status: boolean) => {
            room.localParticipant.videoTracks.forEach((publication: any) => {
                if (status) {
                    publication.track.enable();
                } else {
                    publication.track.disable();
                }
            });
        },
        [room]
    );

    const disconnectVideoRoomFun = React.useCallback((retryConnection: boolean = false) => {
        logger(`videoRoom video connect disconnectVideoRoomFun retryConnection-`, retryConnection);
        setRoom((currentRoom: any) => {
            if (
                currentRoom &&
                currentRoom.localParticipant.state === "connected"
            ) {
                currentRoom.localParticipant.tracks.forEach(
                    (trackPublication: any) => {
                        trackPublication.track.stop();
                    }
                );
                currentRoom.disconnect();
                return null;
            } else {
                return currentRoom;
            }
        });
        if (retryConnection) {
            retryVideoConnection();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logger]);

    const connectVideoRoomFun = React.useCallback((connectionCount: number = 1) => {
        logger(`videoRoom video connect triggered connectionCount: ${connectionCount} isKiosk: ${isKiosk} isHandOffRoom: ${isHandOffRoom} connectVideoRoomFlag: ${connectVideoRoom}  twilioToken`, twilioToken);
        if (connectVideoRoom) {
            setConnectVideoRoom(false);
            let finalAudioInputDevice: any = localStorage.getItem('selectedAudioInputDevice') ? localStorage.getItem('selectedAudioInputDevice') : "";
            let finalAudioOutputDevice: any = localStorage.getItem('selectedAudioOutputDevice') ? localStorage.getItem('selectedAudioOutputDevice') : "";
            let finalVideoDevice: any = localStorage.getItem('selectedVideoDevice') ? localStorage.getItem('selectedVideoDevice') : "";
            logger('videoRoom createLocalTracks triggered', "Triggered");
            createLocalTracks({ audio: { deviceId: finalAudioInputDevice }, video: { deviceId: finalVideoDevice } }).then(localTracks => {
                logger('videoRoom createLocalTracks successful', localTracks);
                setTimeout(() => {
                    if (roomDataRef.current && roomDataRef.current !== null) {
                        logger("got the room details within timeout", { "isKiosk": isKiosk, "isHandOffRoom": isHandOffRoom, "roomName": roomName ? roomName : "", "roomSid": roomSid ? roomSid : "", "agentName": agentName ? agentName : "", "agentId": agentId ? agentId : "", "locationId": locationId ? locationId : "", "notificationId": notificationId ? notificationId : "" })
                    } else {
                        DisplayToaster("bg-warning", "Trying to connect, please wait for some time");
                        logger("Could not able to connect the video within timeout", { "isKiosk": isKiosk, "isHandOffRoom": isHandOffRoom, "roomDetails": roomDataRef.current ? roomDataRef.current : "", "roomName": roomName ? roomName : "", "roomSid": roomSid ? roomSid : "", "agentName": agentName ? agentName : "", "agentId": agentId ? agentId : "", "locationId": locationId ? locationId : "", "notificationId": notificationId ? notificationId : "" })
                    }
                }, 30000);
                Video.connect(twilioToken, { name: roomName, tracks: localTracks, notifyWarnings: ['recording-media-lost'] }).then((room: any) => {
                    logger('videoRoom video connect success', room);
                    const callConnectedTimeStamp = Date.now().toString();
                    if (localStorage.getItem(room.sid) === null) {
                        localStorage.setItem(room.sid, callConnectedTimeStamp)
                    }
                    room.on('trackSubscribed', (track: any) => {
                        if (track.kind === 'audio') {
                            setLocalAudioTrack(track);
                            const audioElement = track.attach();
                            if (getMobileOperatingSystem() !== "Android") {
                                try {
                                    audioElement.setSinkId(finalAudioOutputDevice).then(() => {
                                        document.body.appendChild(audioElement);
                                    });
                                } catch {
                                    // DisplayToaster("bg-danger", "The operating system/browser does not support selecting the audio device in video room intial setup");
                                    console.log("The operating system/browser does not support selecting the audio device in video room intial setup");
                                }
                            }
                        }
                    });
                    room.on("disconnected", (room: any, error: any) => {
                        logger('videoRoom videoConnect disconnected event', error);
                        if (error && error.code === 53118) {
                            isHandOffRoom && terminateHandOffCall(false);
                            isQrTenantRoom && terminateQrRoomCall(false, "ended by host");
                        } else if (error && error.code === 53118) {
                            logger('videoRoom disconnected error', error);
                        } else {
                            disconnectVideoRoomFun(true);
                        }
                    });
                    setRoom(room);
                    roomDataRef.current = room;
                    setLocalParticipantInfo(room.localParticipant);
                }).catch((error) => {
                    logger('videoRoom video connect error', error);
                    disconnectVideoRoomFun(true);
                });
            }).catch((error) => {
                logger('videoRoom createLocalTracks error', error);
                disconnectVideoRoomFun(true);
            });
        } else {
            logger('videoRoom connectVideoRoom', connectVideoRoom);
        }
    }, [agentId, agentName, connectVideoRoom, disconnectVideoRoomFun, isHandOffRoom, isKiosk, isQrTenantRoom, locationId, logger, notificationId, roomName, roomSid, setLocalParticipantInfo, terminateHandOffCall, terminateQrRoomCall, twilioToken]);


    React.useEffect(() => {
        if (room && room.localParticipant) {
            room.localParticipant.tracks?.forEach((publication: any) => {
                publication?.on('warning', (name: string) => {
                    if (name === 'recording-media-lost') {
                        logger('Video loss error', `LocalTrack ${publication?.track?.name} is not recording media.`);
                        const timer = setTimeout(() => {
                            DisplayToaster("bg-danger", "Something went wrong with your video feed, Please check your camera/internet connection");
                        }, 5000);

                        publication?.once('warningsCleared', () => {
                            logger('video loss cleared', `LocalTrack ${publication?.track?.name} warnings have cleared!`);
                            clearTimeout(timer);
                        });
                    }
                });
            });
        }
    }, [room]);

    const retryVideoDisconnect = React.useCallback(() => {
        logger("videoRoom retry video disconnect triggered", {});
        PostApiMethod("tenant/callHandOff/retry-end-call", { agent_id: agentId, room_sid: roomSid }).then((result) => {
            logger("videoRoom retry video disconnect result", result);
            if (result.data.status === "Success") {
                DisplayToaster("bg-success", result.data.data ? result.data.data : "Call disconnected due to error.", "10000");
            } else {
                DisplayToaster("bg-danger", "Something went wrong!", "10000");
            }
        }).catch((error) => {
            logger("videoRoom retry video disconnect error", error);
            DisplayToaster("bg-danger", DisplayErrorMessages(error) ? DisplayErrorMessages(error) : "Something went wrong!", "10000");
        })
    }, [agentId, logger, roomSid]);

    // Retry VideoConnection until
    const retryVideoConnection = React.useCallback(() => {
        setConnectVideoRoomCount((previousCount: number) => {
            if (previousCount <= 3) {
                setConnectVideoRoom(true);
                connectVideoRoomFun(previousCount);
            } else {
                DisplayToaster("bg-danger", "Unable to connect video room, hence disconnecting the call.", "10000");
                setTimeout(() => {
                    if (isKiosk || isHandOffRoom || isQrTenantRoom) {
                        // call the post API to end the call
                        if (!isQrTenantRoom) {
                            retryVideoDisconnect();
                        }
                        if (isHandOffRoom && terminateHandOffCall) {
                            terminateHandOffCall(false, false);
                        }

                        if (isQrTenantRoom && terminateQrRoomCall) {
                            terminateQrRoomCall(false, "", false);
                        }
                    } else {
                        // Do call the disconnect function.
                        dispatch(triggerVideoCallEndFunction({ notificationId: notificationId ? notificationId : locationId, isAutomateTerminate: false, IsManualCall: notificationId ? "no" : "yes", isTenant: "" }));
                    }
                }, 5000);
            }
            return previousCount + 1;
        })
    }, [connectVideoRoomFun, isKiosk, isHandOffRoom, isQrTenantRoom, terminateHandOffCall, terminateQrRoomCall, retryVideoDisconnect, dispatch, notificationId, locationId])

    React.useEffect(() => {
        logger('videoRoom initial phase twilioToken-', twilioToken);
        if (twilioToken) {
            connectVideoRoomFun();
        }
        return () => {
            if (twilioToken) {
                disconnectVideoRoomFun();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [twilioToken]);

    const removeDuplicates = (arr: any) => {
        return arr.filter((value: any, index: any, self: any) =>
            index === self.findIndex((t: any) => (
                t.identity === value.identity
            ))
        )
    }

    const participantConnected = React.useCallback(
        (participant: any) => {
            setParticipants((prevParticipants: any) => {
                // if (prevParticipants && prevParticipants.length > 0 && prevParticipants[0].identity === participant.identity) {
                //     return [...[], participant];
                // } else {
                return removeDuplicates([...prevParticipants, participant])
                // return [...prevParticipants, participant];
                // }
            });
            participantConnectedRef.current = true;
        },
        []
    );

    const remoteParticipantConnected = () => {
        isQrTenantRoom && remoteParticipantConnectedFun(true);
    }

    const participantDisconnected = React.useCallback(
        (participant: any) => {
            // participantDisconnectedHandler();
            setParticipants((prevParticipants: any) => {
                return prevParticipants.filter((p: any) => p.identity !== participant.identity);
            });
            detachParticipantTracks(participant);
        },
        [detachParticipantTracks]
    );

    React.useEffect(() => {
        if (room && !participantConnectedRef.current) {
            room.on("participantConnected", participantConnected);
            room.on("participantConnected", remoteParticipantConnected);
            room.on("participantDisconnected", participantDisconnected);
            room.participants.forEach(participantConnected);
        }
        return () => {
            if (room) {
                // participantConnectedRef.current = false;
                // room.off("participantConnected", participantConnected);
                // room.off("participantDisconnected", participantDisconnected);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [room]);

    const detachCurrentVideoCall = React.useCallback(() => {
        if (room) {
            room.localParticipant.tracks.forEach((publication: any) => {
                if (publication.track.kind === 'audio' || publication.track.kind === 'video') {
                    publication.track.stop();
                    const attachedElements = publication.track.detach();
                    attachedElements.forEach((element: any) => element.remove());
                }
            });
            room.on("disconnected", () => {
                detachParticipantTracks(room.localParticipant);
                room.participants.forEach(detachParticipantTracks);
                setRoom(null);
            });
            detachParticipantTracks(room.localParticipant);
            room.participants.forEach(detachParticipantTracks);
            room.disconnect();
            setRoom(null);
            setLocalParticipantInfo(null);
            room.off("participantConnected", participantConnected);
            room.off("participantDisconnected", participantDisconnected);
        }
    }, [detachParticipantTracks, participantConnected, participantDisconnected, room, setLocalParticipantInfo]);

    const leaveRoom = React.useCallback(() => {
        if (room) {
            room.localParticipant.tracks.forEach((publication: any) => {
                if (publication.track.kind === 'audio' || publication.track.kind === 'video') {
                    publication.track.stop();
                    const attachedElements = publication.track.detach();
                    attachedElements.forEach((element: any) => element.remove());
                }
            });
            room.disconnect();
            setLocalParticipantInfo(null);
            setRoom(null);
            room.on("disconnected", () => {
                setLocalParticipantInfo(null);
                setRoom(null);
            });
        }
    }, [room, setLocalParticipantInfo])

    React.useEffect(() => {
        terminateVideoCallRef.current = detachCurrentVideoCall;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detachCurrentVideoCall]);

    React.useEffect(() => {
        if (holdVideoCallRef) {
            holdVideoCallRef.current = leaveRoom;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaveRoom])

    const handleAudioMute = React.useCallback(() => {
        setAudioDisable((disable) => !disable);
        muteAudioTracks(audioDisable);
    }, [audioDisable, muteAudioTracks]);

    const handleVideoMute = React.useCallback(() => {
        setVideoDisable((disable) => !disable);
        muteVideoTracks(videoDisable);
    }, [muteVideoTracks, videoDisable]);

    const handleCameraSwap = React.useCallback(async () => {
        if (room && room.localParticipant) {
            room.localParticipant.tracks.forEach((publication: any) => {
                if (publication.track.kind === 'video') {
                    publication.track?.restart({
                        ...(DEFAULT_VIDEO_CONSTRAINTS as {}),
                        facingMode: camerasFacingMode ? "user" : "environment",
                    });
                    setCamerasFacingMode((previousCamerasFacingMode: boolean) => {
                        return !previousCamerasFacingMode
                    })
                }
            });
        }
    }, [camerasFacingMode, room]);

    React.useEffect(() => {
        if (room && room.localParticipant) {
            room.localParticipant.tracks?.forEach((publication: any) => {
                publication?.on('warning', (name: string) => {
                    if (name === 'recording-media-lost') {
                        logger('Video loss error', `LocalTrack ${publication?.track?.name} is not recording media.`);
                        const timer = setTimeout(() => {
                            DisplayToaster("bg-danger", "Something went wrong with your video feed, Please check your camera/internet connection");
                        }, 5000);

                        publication?.once('warningsCleared', () => {
                            logger('video loss cleared', `LocalTrack ${publication?.track?.name} warnings have cleared!`);
                            clearTimeout(timer);
                        });
                    }
                });
            });
        }
    }, [room]);

    React.useEffect(() => {
        if (notificationsSliceSelector.triggersetSinkId) {
            if (room && room.localParticipant) {
                room.localParticipant.tracks.forEach((publication: any) => {
                    if (Object.keys(notificationsSliceSelector.notificationAudioDevice)[0] === "selectedVideoDevice" && publication.track.kind === 'video' && localStorage.getItem('selectedVideoDevice')) {
                        publication.track?.restart({
                            ...(DEFAULT_VIDEO_CONSTRAINTS as {}),
                            deviceId: localStorage.getItem('selectedVideoDevice'),
                        }).then((res: any) => {
                            dispatch(setEnableDisableDropDown(""));
                        }).catch((err: any) => {
                            console.log(err, "err")
                        });
                    } else if (Object.keys(notificationsSliceSelector.notificationAudioDevice)[0] !== "selectedVideoDevice" && publication.track.kind === 'audio' && localStorage.getItem('selectedAudioInputDevice')) {
                        // console.log("setting input audio device : ", localStorage.getItem('selectedAudioInputDevice'))
                        publication.track?.restart({
                            ...(DEFAULT_AUDIO_CONSTRAINTS as {}),
                            deviceId: localStorage.getItem('selectedAudioInputDevice'),
                        }).then((res: any) => {
                            dispatch(setEnableDisableDropDown(""));
                        }).catch((err: any) => {
                            console.log(err, "err")
                        });
                    }
                });
                if (Object.keys(notificationsSliceSelector.notificationAudioDevice)[0] === "selectedAudioOutputDevice" && localAudioTrack && localStorage.getItem('selectedAudioOutputDevice')) {
                    const audioElement = localAudioTrack.attach();
                    // console.log("setting output audio device : ", localStorage.getItem('selectedAudioOutputDevice'));
                    try {
                        audioElement.setSinkId(localStorage.getItem('selectedAudioOutputDevice')).then(() => {
                            document.body.appendChild(audioElement);
                            dispatch(setEnableDisableDropDown(""));
                        });
                    } catch (err: any) {
                        // console.log(err, 'err in video room');
                        DisplayToaster("bg-danger", "The operating system/browser does not support selecting the audio device");
                    }
                }
            }
        }
    }, [notificationsSliceSelector.triggersetSinkId]);

    React.useEffect(() => {
        if (inHoldDuration) {
            setTimer(inHoldDuration);
        }
    }, [inHoldDuration])

    React.useEffect(() => {
        let interval: any = 0;
        if (startTimer) {
            interval = setInterval(() => {
                setTimer((perviousTime) => {
                    return perviousTime + 1000;
                });
            }, 1000);
        } else {
            clearInterval(interval);
        }
        return () => {
            clearInterval(interval)
        };
    }, [startTimer]);

    const remoteParticipants = participants.map(
        (participantss: any, index: number) => (
            <div key={participantss.sid + index} className={
                (isKiosk)
                    ? ((participants && participants.length > 1) ? "multiple-kiosk-video-wrapper kiosk-video-wrapper" : "single-kiosk-video-wrapper kiosk-video-wrapper") :
                    (isHandOffRoom || isQrTenantRoom) ? (participants && participants.length > 1 ? "multiple-tenant-video-wrapper tenant-video-wrapper" : "single-tenant-video-wrapper tenant-video-wrapper")
                        : ((!isKiosk && participants.length) > 1 ? "multiple-tenant-video-wrapper tenant-video-wrapper" : "single-tenant-video-wrapper tenant-video-wrapper")
            }>
                <Participant
                    key={participantss.sid + index}
                    keys={participantss.sid}
                    participant={participantss}
                    classNames={
                        (isKiosk)
                            ? ((participants && participants.length > 1) ? `MultiplekioskRemoteParticipantVideoCard ${participantss.sid}` : `kioskRemoteParticipantVideoCard ${participantss.sid}`) :
                            (isHandOffRoom || isQrTenantRoom) ? (participants && participants.length > 1 ? `MultipletenantRemoteParticipantVideoCard ${participantss.sid}` : `singletenantRemoteParticipantVideoCard ${participantss.sid}`)
                                : ((!isKiosk && participants.length) > 1 ? `MultipleremoteParticipantVideoCard ${participantss.sid}` : `singleremoteParticipantVideoCard ${participantss.sid}`)
                    }
                    isLocalParticipant={false}
                />
            </div>
        )
    );
    const participantsAudio = participants.map(
        (participantss: any, index: number) => (
            <ParticipantAudioTracks participant={participantss} key={participantss.sid + index} />
        )
    );


    const [videoCallHandOffNumber, setVideoCallHandOffNumber] = React.useState<any>();

    const OnChangeStoreHandOffContact = React.useCallback(
        (event: any) => {
            setVideoCallHandOffNumber(event.target.value);
            setHandOffContactError(false);
            setContactMinMaxError(false);
        },
        [setVideoCallHandOffNumber]
    );

    const makeVideoCallHandoff = React.useCallback((e: any) => {
        setHandOffLoader(true);
        logger("videoRoom callHandOff triggered", "Triggered");
        if (e)
            e.preventDefault();
        if (!RevertFormattedPhoneNumber(videoCallHandoffNumberRef.current ? videoCallHandoffNumberRef.current : '')) {
            setHandOffLoader(false);
            setHandOffContactError(true);
            return false;
        }
        else if (videoCallHandoffNumberRef.current && (RevertFormattedPhoneNumber(videoCallHandoffNumberRef.current).length < 10 || RevertFormattedPhoneNumber(videoCallHandoffNumberRef.current).length > 11)) {
            setHandOffLoader(false);
            setContactMinMaxError(true);
            return false;
        } else {
            let postData = {
                room_name: roomName,
                contact: RevertFormattedPhoneNumber("1" + videoCallHandoffNumberRef.current),
                location: locationId,
                room_sid: roomSid,
                notification_id: notificationId,
                agent_id: agentId,
            }
            PostApiMethod("tenant/callHandOff/send-link", postData).then((res) => {
                // if (res && res.data && res.data.data && res.data.data.url) {
                //     const myUrl1 = new URL(res.data.data.url).searchParams;
                //     setCallHandOffId(myUrl1.get('id') ? myUrl1.get('id')! : "");
                // }
                logger("videoRoom callHandOff result", res);
                dispatch(setInHandOffCall(locationId));
                if (res.data.status === "Success") {
                    setHandOffLoader(false);
                    DisplayToaster("bg-success", "Hand-off link has been shared to Tenant's contact number successfully!");
                    setShowPopUp(false);
                }
            }).catch((err) => {
                logger("videoRoom callHandOff error", err);
                setHandOffLoader(false);
                DisplayToaster("bg-danger", DisplayErrorMessages(err) ? DisplayErrorMessages(err) : "Something went wrong!");
                setShowPopUp(false);
            })
            setVideoCallHandOffNumber("");
        }

    }, [agentId, locationId, dispatch, logger, notificationId, roomName, roomSid]);

    React.useEffect(() => {
        videoCallHandoffNumberRef.current = videoCallHandOffNumber;
    }, [videoCallHandOffNumber]);

    const addThirdUsers = React.useCallback((agentDetails: any) => {
        logger('videoRoom addThirdUsers trigger agentDetails', agentDetails);
        setAddThirdUserFlag(false);
        addParticipantVideoCall(roomName, agentDetails._id, locationId, roomSid, isQrScanNotification, "");
    }, [addParticipantVideoCall, locationId, logger, roomName, roomSid, isQrScanNotification]);

    return (
        <div>
            {twilioToken ? (

                <div className={`${!isKiosk && ((isQrTenantRoom || isHandOffRoom)) ? 'room zoom-qr-room' : 'room zoom-agentscreen-video'}`}>
                    {showPopUp &&
                        (<AppModal
                            title="Are you sure to hand-off this call?"
                            bodyText=""
                            primaryButtonLabel={"Share"}
                            secondaryButtonLabel={"Cancel"}
                            primaryButtonEventEmitter={(e: any) => makeVideoCallHandoff(e)}
                            secondaryButtonEventEmitter={() => { setShowPopUp(false); setVideoCallHandOffNumber(""); setHandOffContactError(false); setContactMinMaxError(false); }}
                            stayAppear={true}
                            handOffLoader={handOffLoader}
                        >
                            <form className="mt-2" onSubmit={(e) => { makeVideoCallHandoff(e) }}>
                                <div className="row">
                                    <div className="col-sm-6 col-md-6 col-lg-6 mt-1">
                                        <label>Tenant's Contact Number</label><span className="text-danger">*</span>
                                    </div>
                                    <div className="col-sm-6 col-md-6 col-lg-6">
                                        <div className="input-group">
                                            <div className="input-group-prepend">
                                                <div className="input-group-text" id="btnGroupAddon"><b>+1</b></div>
                                            </div>
                                            <InputMask
                                                type="text"
                                                name="contact"
                                                defaultValue={videoCallHandOffNumber}
                                                onChange={(e) => { OnChangeStoreHandOffContact(e) }}
                                                className="form-control"
                                                mask="(999)-999-99999" maskChar={null}
                                            // onKeyPress={numberOnly}
                                            />
                                        </div>

                                        {handOffContactError && <small className="text-danger">Tenant's Contact Number is required</small>}
                                        {contactMinMaxError && <small className="text-danger">Tenant's Contact Number should be 10 or 11 digits</small>}
                                    </div>
                                </div>
                            </form>
                        </AppModal>
                        )}
                    <div
                        className={
                            (isKiosk)
                                ? "remote-participant-section kiosk-remote-participant-section"
                                : (isHandOffRoom) ? ((participants && participants.length > 1) ? "Multiple-tenant-remote-participant-section" : "tenant-remote-participant-section")
                                    : ((participants && participants.length > 1) ? "remote-participant-section" : (isQrTenantRoom) ? "qr-remote-participant-section twilio-screen" : "single-remote-participant-section")
                        }
                    >
                        {participants.length > 0 ? (
                            remoteParticipants
                        ) : (
                            <>
                                {isKiosk ? (
                                    <AppLoader
                                        removeFixedHeight={true}
                                        customClassName={
                                            "kiosk-remote-participant"
                                        }
                                        customStyle={kioskColorInfo.current}
                                    />
                                ) : (
                                    <AppLoader />
                                )}
                            </>
                        )}
                    </div>
                    <div
                        className={
                            (isKiosk)
                                ? " d-flex justify-content-between kioskLocalParticipantVideoCard" :
                                (isHandOffRoom || isQrTenantRoom) ? ""
                                    : " d-flex justify-content-between local-participant-section agent-local-participant-section"
                        }
                    >
                        {!isKiosk && room ? (
                            <>
                                {/* for Agent Screen and for desktop screen */}
                                {!isHandOffRoom && !isQrTenantRoom && <div className={"video-call-action-button-desktop mt-5"}>
                                    <div className={" d-flex align-items-center"}>
                                        <Button
                                            className="ml-1"
                                            title="End call"
                                            onClick={() => {
                                                // eslint-disable-next-line no-lone-blocks
                                                {
                                                    terminateVideoCall(
                                                        notificationId
                                                            ? notificationId
                                                            : locationId,
                                                        false,
                                                        notificationId
                                                            ? "no"
                                                            : "yes"
                                                    )
                                                }
                                            }}
                                            variant="danger"
                                        >
                                            <i className="bi bi-telephone-fill"></i>
                                        </Button>
                                        <Button
                                            className="ml-1 mr-1"
                                            onClick={() => handleAudioMute()}
                                            title="Mic mute / unmute"
                                            variant={
                                                audioDisable
                                                    ? "warning"
                                                    : "secondary"
                                            }
                                        >
                                            <i
                                                className={
                                                    audioDisable
                                                        ? "bi bi-mic-mute-fill"
                                                        : "bi bi-mic-fill"
                                                }
                                            ></i>
                                        </Button>
                                        <Button
                                            className="mr-1"
                                            onClick={() => {
                                                handleVideoMute();
                                            }}
                                            variant={
                                                videoDisable
                                                    ? "warning"
                                                    : "secondary"
                                            }
                                            title='Enable/Disable video'
                                        >
                                            <i
                                                className={
                                                    videoDisable
                                                        ? "bi bi-camera-video-off-fill"
                                                        : "bi bi-camera-video-fill"
                                                }
                                            ></i>
                                        </Button>
                                        {
                                            (addParticipant) ?
                                                <div className="btn-tooltip-container">
                                                    <Button
                                                        className="mr-1"
                                                        onClick={() => { setAddThirdUserFlag(true) }}
                                                        variant="secondary"
                                                        title="Add agent"
                                                    >
                                                        <i className="bi bi-person-plus-fill"></i>
                                                    </Button>
                                                    <AgentStatusInfoTooltip tooltipClass="tooltiptext" />
                                                </div>
                                                :
                                                <></>
                                        }
                                        {/* <Button
                                            className="mr-1"
                                            onClick={() => { setLocalDeviceFlag(true) }}
                                            variant="secondary"
                                            title="Select Device"
                                        >
                                            <i className="bi bi-sliders"></i>
                                        </Button> */}
                                        {!isQrScanNotification &&
                                            <Button
                                                className="mr-1"
                                                onClick={() => {
                                                    setShowPopUp(true)
                                                }}
                                                variant="secondary"
                                                title="Call hand-off"
                                            >
                                                <i className="bi bi-share"></i>
                                            </Button>
                                        }

                                        {
                                            (!notificationsSliceSelector.holdCallDetails ||
                                                (notificationsSliceSelector.holdCallDetails &&
                                                    notificationsSliceSelector.holdCallDetails.inCallRefId &&
                                                    notificationsSliceSelector.inCallNotificationIdRef &&
                                                    notificationsSliceSelector.inCallNotificationIdRef ===
                                                    notificationsSliceSelector.holdCallDetails.inCallRefId)) ? (
                                                <Button
                                                    className="mr-1"
                                                    onClick={() => putCallOnHold('video')}
                                                    variant="secondary"
                                                    title="Hold"
                                                >
                                                    <i className="bi bi-pause-circle-fill"></i>
                                                </Button>
                                            ) : (
                                                <></>
                                            )
                                        }
                                        <Button
                                            className="mr-1"
                                            variant="secondary"
                                            disabled
                                        >
                                            {" "}
                                            {(
                                                "0" +
                                                Math.floor((timer / 60000) % 60)
                                            ).slice(-2)}
                                            :
                                            {(
                                                "0" +
                                                Math.floor((timer / 1000) % 60)
                                            ).slice(-2)}
                                        </Button>
                                    </div >
                                </div >
                                }
                                <div className={(isHandOffRoom || isQrTenantRoom) ? "ml-auto Participant-video-frame px-4 pt-5" : "ml-auto Participant-video-frame px-4"}>
                                    {room && (
                                        <>
                                            <div className="participants-audio">
                                                {(participantsAudio)}
                                            </div>
                                            <Participant
                                                keys={room.localParticipant.sid}
                                                participant={room.localParticipant}
                                                muteval={audioDisable}
                                                classNames="localParticipantVideoCard"
                                                isLocalParticipant={true}
                                            />
                                        </>

                                    )}
                                </div>
                                {/* Hand-off tenant / QrScan tenant room Desktop view */}
                                {
                                    (isHandOffRoom || isQrTenantRoom) &&
                                    <div className={"zoom-room-controls video-call-action-button-desktop"}>
                                        <div>
                                            <Button
                                                className="ml-1"
                                                title="End call"
                                                onClick={() => {
                                                    isHandOffRoom ? terminateHandOffCall(true) : terminateQrRoomCall(true)
                                                }}
                                                variant="danger"
                                            >
                                                <i className="bi bi-telephone-fill"></i>
                                            </Button>
                                            <Button
                                                className="ml-1 mr-1"
                                                onClick={() => handleAudioMute()}
                                                title="Mic mute / unmute"
                                                variant={
                                                    audioDisable
                                                        ? "warning"
                                                        : "secondary"
                                                }
                                            >
                                                <i
                                                    className={
                                                        audioDisable
                                                            ? "bi bi-mic-mute-fill"
                                                            : "bi bi-mic-fill"
                                                    }
                                                ></i>
                                            </Button>
                                            <Button
                                                className="mr-1"
                                                onClick={() => {
                                                    handleVideoMute();
                                                }}
                                                variant={
                                                    videoDisable
                                                        ? "warning"
                                                        : "secondary"
                                                }
                                                title='Enable/Disable video'
                                            >
                                                <i
                                                    className={
                                                        videoDisable
                                                            ? "bi bi-camera-video-off-fill"
                                                            : "bi bi-camera-video-fill"
                                                    }
                                                ></i>
                                            </Button>
                                            <Button
                                                className="mr-1"
                                                onClick={() => {
                                                    handleCameraSwap();
                                                }}
                                                variant={"secondary"}
                                                title='Camera swap'
                                            >
                                                <i className="bi bi-phone-flip"></i>
                                            </Button>
                                            <Button
                                                className="mr-1"
                                                variant="secondary"
                                                disabled
                                            >
                                                {" "}
                                                {(
                                                    "0" +
                                                    Math.floor((timer / 60000) % 60)
                                                ).slice(-2)}
                                                :
                                                {(
                                                    "0" +
                                                    Math.floor((timer / 1000) % 60)
                                                ).slice(-2)}
                                            </Button>
                                        </div >
                                    </div >
                                }

                                {/* Mobile view Starts for local participant video and all the option buttons */}
                                <div className={(isHandOffRoom || isQrTenantRoom) ? "video-call-action-button-mobile mt-5" : "video-call-action-button-mobile"}>
                                    <div className="d-flex flex-wrap align-items-center justify-content-center mb-2">
                                        <Button
                                            className="ml-1 mt-1"
                                            onClick={() => {
                                                (!isHandOffRoom && !isQrTenantRoom) ?
                                                    terminateVideoCall(
                                                        notificationId
                                                            ? notificationId
                                                            : locationId,
                                                        false,
                                                        notificationId
                                                            ? "no"
                                                            : "yes"
                                                    ) : (isHandOffRoom ? terminateHandOffCall(true) : terminateQrRoomCall(true))
                                            }}
                                            variant="danger"
                                            title="End call"
                                        >
                                            <i className="bi bi-telephone-fill"></i>
                                        </Button>
                                        <Button
                                            className="ml-1 mr-1 mt-1"
                                            onClick={() => {
                                                handleAudioMute();
                                            }}
                                            variant={
                                                audioDisable
                                                    ? "warning"
                                                    : "secondary"
                                            }
                                            title="Mic mute / unmute"
                                        >
                                            <i
                                                className={
                                                    audioDisable
                                                        ? "bi bi-mic-mute-fill"
                                                        : "bi bi-mic-fill"
                                                }
                                            ></i>
                                        </Button>
                                        <Button
                                            className="mr-1 mt-1"
                                            onClick={() => {
                                                handleVideoMute();
                                            }}
                                            variant={
                                                videoDisable
                                                    ? "warning"
                                                    : "secondary"
                                            }
                                            title='Enable/Disable video'
                                        >
                                            <i
                                                className={
                                                    videoDisable
                                                        ? "bi bi-camera-video-off-fill"
                                                        : "bi bi-camera-video-fill"
                                                }
                                            ></i>
                                        </Button>
                                        {(isHandOffRoom || isQrTenantRoom) ?
                                            <Button
                                                className="mr-1"
                                                onClick={() => {
                                                    handleCameraSwap();
                                                }}
                                                variant={"secondary"}
                                                title='Camera swap'
                                            >
                                                <i className="bi bi-phone-flip"></i>
                                            </Button>
                                            :
                                            <>
                                                {!isQrScanNotification &&
                                                    <Button
                                                        className="mr-1 mt-1"
                                                        onClick={() => { setShowPopUp(true) }}
                                                        variant="secondary"
                                                        title="Call hand-off"
                                                    >
                                                        <i className="bi bi-share"></i>
                                                    </Button>
                                                }
                                                <Button
                                                    className="mr-1 mt-1"
                                                    onClick={() => { setAddThirdUserFlag(true); }}
                                                    title="Add agent"
                                                    variant="secondary">
                                                    <i className="bi bi-person-plus-fill"></i>
                                                </Button>
                                                {/* <Button
                                                    className="mr-1"
                                                    onClick={() => { setLocalDeviceFlag(true) }}
                                                    variant="secondary"
                                                    title="Select Device"
                                                >
                                                    <i className="bi bi-sliders"></i>
                                                </Button> */}
                                            </>
                                        }
                                        <Button
                                            className="mr-1 mt-1"
                                            variant="secondary"
                                            disabled
                                        >
                                            {(
                                                "0" +
                                                Math.floor((timer / 60000) % 60)
                                            ).slice(-2)}
                                            :
                                            {(
                                                "0" +
                                                Math.floor((timer / 1000) % 60)
                                            ).slice(-2)}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {room && (
                                    <>
                                        <div className="participants-audio">
                                            {(participantsAudio)}
                                        </div>
                                        <Participant
                                            keys={room.localParticipant.sid}
                                            participant={room.localParticipant}
                                            muteval={audioDisable}
                                            classNames="localParticipantVideoCard"
                                            isLocalParticipant={true}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div >
                    {agentName && (
                        <div className="kioskAgentName">
                            <p style={kioskColorInfo.current} className="AgentNameDisplay">
                                You are connected with <i>{agentName}</i>
                            </p>
                        </div>
                    )}
                </div >
            ) : (
                <div className="logo">
                    <img
                        src={vrm_large_logo}
                        className="vrm_large_logo"
                        alt="temp"
                    />
                </div>
            )
            }
            {
                (addThirdUserFlag) ?
                    <AppModal
                        title={""}
                        bodyText={""}
                        showCloseButton={true}
                        closeButtonEventEmitter={() => { setAddThirdUserFlag(false); }}
                    >
                        <Teams addAgentModel={true} addAgentModelCallBackFunction={(agentDetails: any) => { addThirdUsers(agentDetails) }} />
                    </AppModal>
                    :
                    <></>
            }
        </div>
    );
};

export default VideoRoom;